import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, FormikTouched } from "formik";
import { Typography, TextField, Button, Box } from "@mui/material";
import { object, string, number } from "yup";
import { translate } from "i18n";
import { acrAPI } from "utils/api";

import { SmtpConfigModel } from "acr-common-models";

export function SmtpConfig(props: any): React.ReactElement{
  const validationSchema = object({
    smtpName: string()
      .required(translate("SMTP hostname is required")),
    port: number().integer()
      .required(translate("a port number is required"))
      .min(1, translate("port value should be between 1 and 65535"))
      .max(65535, translate("port value should be between 1 and 65535")),
    password: string(),
      //.required(translate("password is required")),
    emailSender: string()
      .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g, translate("bad email format")),
      //.email(translate("bad email format")),
      //.required(translate("an email address is required"))
    source: string()
      .required(translate("source label is required"))
      .max(20, translate("source label cannot be more than 20 characters")),
  });

  const [smtpConfig, setSmtpConfig] = useState<any>({smtpName: "Loading...", port: 0, password: "" , emailSender: "Loading...", source: "Loading..."});
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const formRef = useRef() as any;

  useEffect(() => {    
    const smtpConfig = acrAPI.smtpConfigSubject.subscribe(updateSmtpConfig);
    acrAPI.getEmailConfiguration();
    return () => {smtpConfig?.unsubscribe()}
  },[]);


  const updateSmtpConfig = (smtpConfigModel: SmtpConfigModel): void => {
    setSmtpConfig({...smtpConfigModel});
  }

  const canSubmit = (errors: any, touched: FormikTouched<SmtpConfigModel>): boolean => {
    return Object.keys(errors).length === 0 && Object.keys(touched).length > 0;
  }

  const handleSubmit = async (values: any): Promise<void> => {
    const { smtpName, port, password, emailSender, id, source } = values;
    if (passwordUpdated)
    {
      await acrAPI.updateSmtpConfiguration({ smtpName, port, password, emailSender, id, source });
    }
    else
    {
      await acrAPI.updateSmtpConfiguration({ smtpName, port, emailSender, id, source });    
    }
  }

    return (
      <Formik
        initialValues={smtpConfig}
        enableReinitialize={true}
        validationSchema={validationSchema}
        innerRef={formRef}
        onSubmit={async (values, { resetForm }) => {
          await handleSubmit(values);
          resetForm({ values });
          setPasswordUpdated(false);
        }}>
        {({ handleSubmit, handleChange, setFieldTouched, touched, values, errors }) => (
          <Form name="settings-email-configuration-form" translate="no" method="POST" onSubmit={handleSubmit}>
            <Box>
              <Typography variant="h5" data-cy="settings-email-configuration-form-title">
                {translate("senderEmailConfiguration")}
              </Typography>
              <Box display="flex" style={{ width: "40em", marginBottom: "0" }}>
                <Field
                  component={TextField}
                  variant="outlined"
                  margin="normal"
                  id="smtpName"
                  name="smtpName"
                  label={translate("smtpName label")}
                  maxLength={50}
                  style={{ width: "18em", marginRight: "2em" }}
                  helperText={errors.smtpName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setFieldTouched("smtpName", true);
                    handleChange(e);
                  }}
                  value={values.smtpName}
                  data-cy={`email-smtp-name`}
                />

                <Field
                  component={TextField}
                  variant="outlined"
                  margin="normal"
                  id="port"
                  name="port"
                  label={translate("Port")}
                  style={{ width: "18em", marginRight: "0em" }}
                  helperText={errors.port}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setFieldTouched("port", true);
                    handleChange(e);
                  }}
                  value={values.port}
                  data-cy={`email-smtp-port`}
                />
              </Box>

              <Box display="flex" style={{ width: "40em", marginRight: "0em", marginBottom: "0" }}>
                <Field
                  component={TextField}
                  variant="outlined"
                  margin="normal"
                  id="emailSender"
                  name="emailSender"
                  label={translate("your-email@gmail.com")}
                  style={{ width: "18em", marginRight: "2em"}}
                  helperText={errors.emailSender}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setFieldTouched("emailSender", true);
                    handleChange(e);
                  }}
                  value={values.emailSender}
                  data-cy={`email-smtp-emailSender`}
                />
                <Field
                  type={"password"}
                  component={TextField}
                  variant="outlined"
                  margin="normal"
                  id="password"
                  name="password"
                  label={translate("password")}
                  style={{ width: "18em", marginRight: "0em" }}
                  helperText={errors.password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setPasswordUpdated(true);
                    setFieldTouched("password", true);
                    handleChange(e);
                  }}
                  value={values.password}
                  data-cy={`email-smtp-password`}
                />
              </Box>
              <Box display="flex" style={{ width: "40em", marginRight: "0em", marginBottom: "0" }}>
                <Field
                  component={TextField}
                  variant="outlined"
                  margin="normal"
                  id="source"
                  name="source"
                  label={translate("source label")}
                  style={{ width: "18em", marginRight: "2em"}}
                  helperText={errors.source}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setFieldTouched("source", true);
                    handleChange(e);
                  }}
                  value={values.source}
                  data-cy={`email-smtp-source`}
                />
              </Box>
              <Box display="flex" style={{ width: "40em", marginRight: "0em", marginBottom: "0" }}>
                <Box display="flex" style={{ width: "18em" ,marginRight: "2em" }}>
                </Box>
                
                <Button
                  type="submit"
                  disabled={!canSubmit(errors, touched)}
                  variant="contained"
                  data-cy="email-settings-configuration-save-button"
                  style={{ width: "15.5em", margin: "1em 0em 0em 0em" }}
                  color="primary">
                  {translate("save")}
                </Button>

              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    );
  }
